// fuel tank — S/N (warehouse). vessels (SAP / name / model) come from the season selector's
// overview, exactly like the infocenter listing; the serials themselves are website-only (D1).
// a vessel can carry several tanks, so each one is its own numbered row. On phones each vessel is a card
// instead (its tanks as stacked field blocks), the search and the two filters sit in a FilterBar, and
// Import / Export / Sync fold into the ⋯ menu.

import { useCallback, useMemo, useState } from 'react';
import { useDeleteFuelTank, useFuelTanks, useImportFuelTanks, useOverview, useSetFuelTank, type FuelTankFields, type FuelTankSn as Tank } from '../features/hooks.js';
import { useAuth } from '../auth/AuthContext.js';
import { useSeason } from '../season/SeasonContext.js';
import { EmptyState, ErrorState, FilterBar, FilterSelect, InlineError, LoadingState, LogoSpin, OverflowActions, PageHeader, SearchInput, useConfirm } from '../components/ui.js';
import { IconDownload, IconTrash, IconUpload } from '../components/icons.js';
import { FuelTankImportModal } from '../components/FuelTankImportModal.js';
import { exportSheet } from '../features/exportSheet.js';
import { api } from '../lib/api.js';
import { useDateFormatter } from '../lib/format.js';
import { localTodayIso } from '../lib/dates.js';
import { useIsPhone } from '../lib/media.js';
import { fold, hasText } from '../lib/search.js';

const MAX_TANKS = 20;
const DELIVERY_OPTS = [
  { value: 'current', label: 'Current production' },
  { value: 'all', label: 'All' },
  { value: 'delivered', label: 'Delivered' },
];

const isISO = (s: string) => /^\d{4}-\d{2}-\d{2}$/.test(s);

/** inline text cell — remounts (key) when the stored value changes so it reflects saves/refetches. */
function CellText({ value, onCommit, label, placeholder, mono, maxLength }: {
  value: string; onCommit: (v: string) => void; label: string; placeholder?: string; mono?: boolean; maxLength?: number;
}) {
  return (
    <input
      key={value}
      className={`field ft-in${mono ? ' ft-mono' : ''}`}
      defaultValue={value}
      aria-label={label}
      placeholder={placeholder}
      maxLength={maxLength}
      onBlur={(e) => { const v = e.target.value; if (v !== value) onCommit(mono ? v.trim() : v); }}
    />
  );
}

/** inline date cell — a native picker for ISO/empty values; plain text for anything else (e.g. "28/7/25"). */
function CellDate({ value, onCommit, label }: { value: string; onCommit: (v: string) => void; label: string }) {
  const asDate = value === '' || isISO(value);
  return (
    <input
      key={value}
      className="field ft-in"
      type={asDate ? 'date' : 'text'}
      defaultValue={value}
      aria-label={label}
      maxLength={40}
      onBlur={(e) => { const v = e.target.value; if (v !== value) onCommit(v); }}
    />
  );
}

export function FuelTankSn() {
  const overview = useOverview(); // follows the season selector
  const df = useDateFormatter();
  const tanks = useFuelTanks();
  const setTank = useSetFuelTank();
  const delTank = useDeleteFuelTank();
  const importTanks = useImportFuelTanks();
  const { can } = useAuth();
  const { activeSeason, seasons } = useSeason();
  const canWrite = can('vesselstatus:write');
  const isPhone = useIsPhone();

  // load another season's vessels on demand, so the importer can match unmatched rows against it.
  const fetchSeasonVessels = useCallback(async (seasonId: string) => {
    const r = await api.get<{ vessels: { sap: string; name: string; model: string }[] }>(
      `/api/overview?season=${encodeURIComponent(seasonId)}`,
    );
    return (r.vessels ?? []).filter((v) => v.name && v.sap).map((v) => ({ sap: v.sap, name: v.name, model: v.model }));
  }, []);

  const [q, setQ] = useState('');
  const [model, setModel] = useState('all');
  const [delivery, setDelivery] = useState<'current' | 'all' | 'delivered'>('current');
  const [showImport, setShowImport] = useState(false);
  const [confirmNode, confirm] = useConfirm();

  // sap → its tanks, ordered by tank number
  const bySap = useMemo(() => {
    const m = new Map<string, Tank[]>();
    for (const t of tanks.data?.items ?? []) {
      const a = m.get(t.sap) ?? [];
      a.push(t);
      m.set(t.sap, a);
    }
    for (const a of m.values()) a.sort((x, y) => x.tankNo - y.tankNo);
    return m;
  }, [tanks.data]);

  const models = useMemo(() => {
    const set = new Set<string>();
    for (const v of overview.data?.vessels ?? []) if (v.name && v.model) set.add(v.model);
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [overview.data]);

  // matching pool for the importer — every named vessel in the overview, regardless of the
  // on-screen filters (the import shouldn't be constrained by the current view).
  const allVessels = useMemo(
    () => (overview.data?.vessels ?? []).filter((v) => v.name && v.sap).map((v) => ({ sap: v.sap, name: v.name, model: v.model })),
    [overview.data],
  );
  const existingBySap = useMemo(() => {
    const m = new Map<string, { tankNo: number; serial: string }[]>();
    for (const t of tanks.data?.items ?? []) {
      const a = m.get(t.sap) ?? [];
      a.push({ tankNo: t.tankNo, serial: t.serial });
      m.set(t.sap, a);
    }
    return m;
  }, [tanks.data]);

  const vessels = useMemo(() => {
    let vs = (overview.data?.vessels ?? []).filter((v) => v.name && v.sap);
    if (delivery === 'current') vs = vs.filter((v) => !v.left);
    else if (delivery === 'delivered') vs = vs.filter((v) => v.left);
    if (model !== 'all') vs = vs.filter((v) => v.model === model);
    const query = fold(q.trim());
    if (query)
      vs = vs.filter(
        (v) => hasText(query, v.name, v.sap, v.model) || (bySap.get(v.sap) ?? []).some((t) => hasText(query, t.serial)),
      );
    return vs;
  }, [overview.data, delivery, model, q, bySap]);

  const addTank = (sap: string) => {
    const cur = bySap.get(sap) ?? [];
    const next = (cur.length ? Math.max(...cur.map((t) => t.tankNo)) : 0) + 1;
    if (next > MAX_TANKS) return;
    setTank.mutate({ sap, tankNo: next, serial: '', receiptDate: '', floatInstalled: '', installDate: '', note: '' });
  };

  // a blank tank (fresh "+ tank") goes straight away; one holding a serial, dates or a comment asks first.
  const removeTank = async (t: Tank, name: string) => {
    const hasData = !!(t.serial || t.receiptDate || t.floatInstalled || t.installDate || t.note);
    if (hasData && !(await confirm({
      title: 'Delete tank',
      message: `Delete tank ${t.tankNo} of ${name}${t.serial ? ` (S/N ${t.serial})` : ''}?`,
      confirmLabel: 'Delete',
      danger: true,
    }))) return;
    delTank.mutate({ sap: t.sap, tankNo: t.tankNo });
  };

  const save = (t: Tank, patch: Partial<FuelTankFields>) =>
    setTank.mutate({
      sap: t.sap,
      tankNo: t.tankNo,
      serial: patch.serial ?? t.serial,
      receiptDate: patch.receiptDate ?? t.receiptDate,
      floatInstalled: patch.floatInstalled ?? t.floatInstalled,
      installDate: patch.installDate ?? t.installDate,
      note: patch.note ?? t.note,
    });

  const busy = overview.isLoading || tanks.isLoading;
  const syncing = overview.isFetching || tanks.isFetching;
  const totalTanks = useMemo(
    () => vessels.reduce((a, v) => a + (bySap.get(v.sap)?.length ?? 0), 0),
    [vessels, bySap],
  );

  // Export the visible vessels + their tanks to an .xlsx (mirrors the on-screen columns / the import).
  const [exporting, setExporting] = useState(false);
  const [exportErr, setExportErr] = useState<unknown>(null);
  const exportExcel = async () => {
    const headers = ['SAP', 'Vessel', 'Model', 'Tank #', 'Serial (S/N)', 'Received', 'Float', 'Installed', 'Comment'];
    const rows = vessels.flatMap((v) => {
      const ts = bySap.get(v.sap) ?? [];
      if (ts.length === 0) return [[v.sap, v.name, v.model, '', '', '', '', '', '']];
      return ts.map((t) => [v.sap, v.name, v.model, t.tankNo, t.serial, t.receiptDate, t.floatInstalled, t.installDate, t.note]);
    });
    const today = localTodayIso();
    setExporting(true); setExportErr(null);
    try { await exportSheet(`Fuel Tank S-N - ${activeSeason || 'all'} - ${today}.xlsx`, 'Fuel Tanks', headers, rows); } catch (e) { setExportErr(e); } finally { setExporting(false); }
  };

  return (
    <>
      <PageHeader title="Fuel Tank - S/N">
        {!isPhone && (
          <>
            <SearchInput value={q} onChange={setQ} placeholder="Search vessel / SAP / model / serial" width={260} />
            <select className="field" value={delivery} onChange={(e) => setDelivery(e.target.value as 'current' | 'all' | 'delivered')} title="Delivery status">
              {DELIVERY_OPTS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <select className="field" value={model} onChange={(e) => setModel(e.target.value)} title="Model">
              <option value="all">All models</option>
              {models.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </>
        )}
        <span className="muted page-count ft-count">{vessels.length} vessel{vessels.length === 1 ? '' : 's'} · {totalTanks} tank{totalTanks === 1 ? '' : 's'}</span>
        <OverflowActions
          title="Fuel Tank - S/N"
          items={[
            canWrite && { label: 'Import from Excel', icon: <IconUpload size={14} />, onClick: () => setShowImport(true), disabled: !overview.data || !tanks.data },
            {
              label: exporting ? 'Exporting…' : 'Export to Excel',
              icon: exporting ? undefined : <IconDownload size={14} />,
              onClick: () => void exportExcel(),
              disabled: exporting || !overview.data || !tanks.data || vessels.length === 0,
            },
            { label: syncing ? 'Syncing…' : 'Sync', icon: syncing ? <LogoSpin size={15} decorative /> : undefined, onClick: () => { void overview.refetch(); void tanks.refetch(); }, disabled: syncing },
          ]}
        />
      </PageHeader>
      {isPhone && (
        <FilterBar
          // the full hint is cut off beside Filters at phone width; SAP and model still match
          search={<SearchInput value={q} onChange={setQ} placeholder="Search vessels or serials" />}
          activeCount={(delivery !== 'current' ? 1 : 0) + (model !== 'all' ? 1 : 0)}
          onClear={() => { setDelivery('current'); setModel('all'); }}
        >
          <FilterSelect label="Show" value={delivery} onChange={(v) => setDelivery(v as 'current' | 'all' | 'delivered')} options={DELIVERY_OPTS} ariaLabel="Delivery status" />
          <FilterSelect label="Model" value={model} onChange={setModel} options={[{ value: 'all', label: 'All models' }, ...models.map((m) => ({ value: m, label: m }))]} />
        </FilterBar>
      )}
      {busy && <LoadingState label="Loading…" />}
      {overview.error && <ErrorState error={overview.error} onRetry={() => void overview.refetch()} />}
      {tanks.error && <ErrorState error={tanks.error} onRetry={() => void tanks.refetch()} />}
      {(setTank.error ?? delTank.error) && (
        <InlineError error={setTank.error ?? delTank.error} onDismiss={() => { setTank.reset(); delTank.reset(); }} />
      )}
      {exportErr ? <InlineError error={exportErr} onDismiss={() => setExportErr(null)} /> : null}

      {showImport && (
        <FuelTankImportModal
          vessels={allVessels}
          existingBySap={existingBySap}
          seasons={seasons}
          activeSeason={activeSeason}
          fetchSeasonVessels={fetchSeasonVessels}
          onClose={() => setShowImport(false)}
          onConfirm={async (items) => (await importTanks.mutateAsync({ items })).count}
        />
      )}

      {overview.data && tanks.data && isPhone && (
        <div className="ft-cards">
          {vessels.map((v) => {
            const rows = bySap.get(v.sap) ?? [];
            return (
              <section key={v.sap} className="ft-card" aria-label={`${v.name}, SAP ${v.sap}`}>
                <div className="ft-card-hd">
                  <div className="ft-card-id">
                    <div className="ft-card-sub">{v.sap}{v.model ? ` · ${v.model}` : ''}</div>
                    <div className="ft-card-name">{v.name}</div>
                  </div>
                  {canWrite && rows.length < MAX_TANKS && (
                    <button className="btn btn-sm ft-add" aria-label={`Add a tank to ${v.name}`} onClick={() => addTank(v.sap)}>+ tank</button>
                  )}
                </div>
                {rows.length === 0 && <div className="muted ft-none">No tanks recorded.</div>}
                {rows.map((t) => (
                  <div key={t.tankNo} className="ft-tank">
                    <div className="ft-tank-hd">
                      <span>Tank {t.tankNo}</span>
                      {canWrite && (
                        <button type="button" className="btn btn-sm btn-ghost btn-del ft-del hit-44" title="Delete this tank" aria-label={`Delete tank ${t.tankNo} of ${v.name}`} onClick={() => void removeTank(t, v.name)}>
                          <IconTrash size={14} /> Delete
                        </button>
                      )}
                    </div>
                    {canWrite ? (
                      <div className="ft-fields">
                        <label className="ft-f ft-f-full"><span className="field-label">Serial (S/N)</span><CellText value={t.serial} mono maxLength={80} placeholder="serial…" label={`Serial, tank ${t.tankNo}, ${v.name}`} onCommit={(x) => save(t, { serial: x })} /></label>
                        <label className="ft-f"><span className="field-label">Received</span><CellDate value={t.receiptDate} label={`Received date, tank ${t.tankNo}, ${v.name}`} onCommit={(x) => save(t, { receiptDate: x })} /></label>
                        <label className="ft-f"><span className="field-label">Installed</span><CellDate value={t.installDate} label={`Installed date, tank ${t.tankNo}, ${v.name}`} onCommit={(x) => save(t, { installDate: x })} /></label>
                        <label className="ft-f ft-f-full"><span className="field-label">Float</span><CellText value={t.floatInstalled} maxLength={120} placeholder="float…" label={`Float, tank ${t.tankNo}, ${v.name}`} onCommit={(x) => save(t, { floatInstalled: x })} /></label>
                        <label className="ft-f ft-f-full"><span className="field-label">Comment</span><CellText value={t.note} maxLength={200} placeholder="comment…" label={`Comment, tank ${t.tankNo}, ${v.name}`} onCommit={(x) => save(t, { note: x })} /></label>
                      </div>
                    ) : (
                      <div className="ft-fields">
                        <div className="ft-f ft-f-full"><span className="field-label">Serial (S/N)</span><span className="ft-val ft-mono">{t.serial || '-'}</span></div>
                        <div className="ft-f"><span className="field-label">Received</span><span className="ft-val">{df.maybe(t.receiptDate) || '-'}</span></div>
                        <div className="ft-f"><span className="field-label">Installed</span><span className="ft-val">{df.maybe(t.installDate) || '-'}</span></div>
                        <div className="ft-f ft-f-full"><span className="field-label">Float</span><span className="ft-val">{t.floatInstalled || '-'}</span></div>
                        {t.note && <div className="ft-f ft-f-full"><span className="field-label">Comment</span><span className="ft-val">{t.note}</span></div>}
                      </div>
                    )}
                  </div>
                ))}
              </section>
            );
          })}
          {vessels.length === 0 && <EmptyState title="No vessels match the current filters." />}
        </div>
      )}

      {overview.data && tanks.data && !isPhone && (
        <>
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th style={{ width: 100 }}>SAP</th>
                  <th style={{ width: 150 }}>Vessel</th>
                  <th style={{ width: 120 }}>Model</th>
                  <th style={{ width: 34 }}>#</th>
                  <th style={{ width: 140 }}>Serial (S/N)</th>
                  <th style={{ width: 132 }}>Received</th>
                  <th style={{ width: 130 }}>Float</th>
                  <th style={{ width: 132 }}>Installed</th>
                  <th>Comment</th>
                  {canWrite && <th style={{ width: 34 }} />}
                </tr>
              </thead>
              <tbody>
                {vessels.map((v) => {
                  const rows = bySap.get(v.sap) ?? [];
                  const groupTop: React.CSSProperties = { borderTop: '2px solid var(--border)' };
                  if (rows.length === 0) {
                    return (
                      <tr key={v.sap} style={groupTop}>
                        <td>{v.sap}</td>
                        <td style={{ fontWeight: 600 }}>{v.name}</td>
                        <td className="muted">{v.model || '-'}</td>
                        <td colSpan={canWrite ? 7 : 6} className="muted" style={{ fontSize: 11 }}>
                          No tanks recorded.
                          {canWrite && <button className="btn btn-sm" style={{ marginLeft: 8 }} onClick={() => addTank(v.sap)}>+ tank</button>}
                        </td>
                      </tr>
                    );
                  }
                  return rows.map((t, i) => {
                    const first = i === 0;
                    return (
                      <tr key={`${v.sap}-${t.tankNo}`} style={first ? groupTop : undefined}>
                        <td>{first ? v.sap : ''}</td>
                        <td style={{ fontWeight: 600 }}>{first ? v.name : ''}</td>
                        <td className="muted">
                          {first ? (v.model || '-') : ''}
                          {first && canWrite && rows.length < MAX_TANKS && (
                            <div><button className="btn btn-sm" style={{ marginTop: 4 }} onClick={() => addTank(v.sap)}>+ tank</button></div>
                          )}
                        </td>
                        <td className="muted">{t.tankNo}</td>
                        {canWrite ? (
                          <>
                            <td><CellText value={t.serial} mono maxLength={80} placeholder="serial…" label={`Serial, tank ${t.tankNo}, ${v.name}`} onCommit={(x) => save(t, { serial: x })} /></td>
                            <td><CellDate value={t.receiptDate} label={`Received date, tank ${t.tankNo}, ${v.name}`} onCommit={(x) => save(t, { receiptDate: x })} /></td>
                            <td><CellText value={t.floatInstalled} maxLength={120} placeholder="float…" label={`Float, tank ${t.tankNo}, ${v.name}`} onCommit={(x) => save(t, { floatInstalled: x })} /></td>
                            <td><CellDate value={t.installDate} label={`Installed date, tank ${t.tankNo}, ${v.name}`} onCommit={(x) => save(t, { installDate: x })} /></td>
                            <td><CellText value={t.note} maxLength={200} placeholder="comment…" label={`Comment, tank ${t.tankNo}, ${v.name}`} onCommit={(x) => save(t, { note: x })} /></td>
                            <td><button className="btn btn-sm btn-ghost btn-del" title="Delete this tank" aria-label={`Delete tank ${t.tankNo} of ${v.name}`} onClick={() => void removeTank(t, v.name)}><IconTrash size={14} /> Delete</button></td>
                          </>
                        ) : (
                          <>
                            <td style={{ fontFamily: 'var(--font-mono)' }}>{t.serial || '-'}</td>
                            <td className="muted">{df.maybe(t.receiptDate) || '-'}</td>
                            <td className="muted">{t.floatInstalled || '-'}</td>
                            <td className="muted">{df.maybe(t.installDate) || '-'}</td>
                            <td className="muted">{t.note || '-'}</td>
                          </>
                        )}
                      </tr>
                    );
                  });
                })}
                {vessels.length === 0 && (
                  <tr><td colSpan={canWrite ? 10 : 9} className="muted" style={{ textAlign: 'center', padding: 20 }}>No vessels match the current filters.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
      {confirmNode}
    </>
  );
}
